import { ShopifyIcon } from '@/components/icons';
import { useAuth } from '@/contexts/auth-context';
import { decideFreePlanAccess } from '@/guards/free-plan-access';
import { OnboardingBrandSwitch } from '@/pages/onboarding/_components/OnboardingBrandSwitch';
import { Box, Flex, Heading, Text } from '@chakra-ui/react';
import { Check } from 'iconoir-react';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { OnboardingContinueButton } from './_components/OnboardingContinueButton';
import { OnboardingLayout } from './_components/OnboardingLayout';
import { ONBOARDING_PATHS } from './_components/onboarding-steps';
import { StatusBadge } from './_components/StatusBadge';
import { useOnboardingSnapshot } from './_hooks/use-onboarding-snapshot';
import { ConnectLaterExit } from './connect/ConnectLaterExit';
import { ShopifyConnect } from './connect/ShopifyConnect';

const ShopifyConnectedCard = ({ onContinue }: { onContinue: () => void }) => {
  const { t } = useTranslation();

  return (
    <Box textAlign="center">
      <Flex
        w="56px"
        h="56px"
        mb="22px"
        mx="auto"
        borderRadius="full"
        bg="primary.900"
        align="center"
        justify="center"
      >
        <Check width={28} height={28} color="white" aria-hidden />
      </Flex>
      <Heading
        as="h1"
        fontFamily="heading"
        fontSize="28px"
        fontWeight="600"
        letterSpacing="-0.02em"
        lineHeight="1.16"
        color="ink"
      >
        {t('onboarding.shopify.success.title')}
      </Heading>
      <Text fontSize="15px" color="inkMuted" mt="10px" lineHeight="1.5" fontWeight="300">
        {t('onboarding.shopify.success.description')}
      </Text>

      <Flex
        mt="24px"
        align="center"
        gap="12px"
        borderWidth="1px"
        borderColor="cardBorder"
        borderRadius="14px"
        bg="white"
        px="16px"
        py="14px"
        textAlign="left"
      >
        <ShopifyIcon boxSize="26px" flex="none" />
        <Box flex="1" minW={0}>
          <Text fontFamily="heading" fontSize="13.5px" fontWeight="600" color="ink" lineClamp={1}>
            {t('onboarding.shopify.success.storeLabel')}
          </Text>
          <Text fontSize="11.5px" color="inkMuted" fontWeight="300">
            {t('onboarding.shopify.success.storeStatus')}
          </Text>
        </Box>
        <StatusBadge tone="ok">{t('onboarding.badge.connected')}</StatusBadge>
      </Flex>

      <OnboardingContinueButton onClick={onContinue}>
        {t('onboarding.shopify.success.continue')}
      </OnboardingContinueButton>
    </Box>
  );
};

/**
 * Shopify connection outside the four steps: where the subscription guard and
 * the 402 interceptor send a brand that must subscribe through Shopify, and
 * where the install of such a brand comes back. A free organization still
 * onboarding is handed over to its own step; one that finished it connects its
 * store from here, or later when it does not sell on Shopify.
 */
const ShopifyOnboardingPage: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const snapshot = useOnboardingSnapshot('connect', { salesChannel: 'shopify' });
  const freePlan = decideFreePlanAccess(user);
  const isConnectedBack = searchParams.get('success') === 'true';

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  if (freePlan.onFreePlan && freePlan.redirectTo) {
    if (isConnectedBack) return <Navigate to={ONBOARDING_PATHS.welcome} replace />;

    const keepsError = freePlan.redirectTo === ONBOARDING_PATHS.connect;
    return <Navigate to={`${freePlan.redirectTo}${keepsError ? location.search : ''}`} replace />;
  }

  if (freePlan.onFreePlan && isConnectedBack) {
    return <Navigate to="/dashboard" replace />;
  }

  if (isConnectedBack) {
    return (
      <OnboardingLayout step="connect" snapshot={snapshot} showProgress={false}>
        <ShopifyConnectedCard onContinue={() => navigate('/dashboard?paywall=plans')} />
      </OnboardingLayout>
    );
  }

  return (
    <OnboardingLayout step="connect" snapshot={snapshot} showProgress={false}>
      <ShopifyConnect error={searchParams.get('error')} />
      {freePlan.onFreePlan && user?.currentBrand?.onboarding?.salesChannel !== 'shopify' && (
        <ConnectLaterExit onLater={() => navigate('/dashboard')} />
      )}
      <OnboardingBrandSwitch />
    </OnboardingLayout>
  );
};

export default ShopifyOnboardingPage;
